/**
 * CARTERA de impagados (pedido de Yago, 2026-08-10).
 *
 * La pantalla con la que Ops DECIDE y hace seguimiento: cada cliente con deuda
 * del ledger de ms-payment, clasificado en su grupo de decisión. Los datos son
 * DERIVADOS en cada consulta (R9 × episodios × estado de póliza), así que esta
 * tabla reconcilia al céntimo con ms-payment por construcción — no es una foto
 * que caduca como un export.
 *
 * Acciones por grupo:
 *   - en cadencia → deep-link a la tarea/episodio;
 *   - pendiente → «Meter en cadencia» (mismas guardas que el barrido);
 *   - write-off → deshabilitado con motivo hasta que ms-payment arregle el 500
 *     del CxC-4 (preferimos enseñar la herramienta grisácea a esconderla);
 *   - disputado → congelado (regla A2), enlace al episodio si existe.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getReceivablesOverview, openEpisodeForLead, writeOffLead } from "../../api/msops";
import RecobrosTabs from "./components/RecobrosTabs";
import { Card, COLORS as UI, ErrorBanner, Loading, Td, Th } from "./components/ui";
import type {
  ReceivableBucket,
  ReceivableRow,
  ReceivablesOverviewResponse,
} from "../../types/msops";

// Sin paleta propia: el kit de `components/ui` es el que usan Tareas y Casos.
// Cartera y Condonados nacieron con estilos sueltos y por eso parecían otro
// sistema (además de que sus rutas se colaron SIN el Layout del panel).
const COLORS = {
  heading: UI.heading,
  muted: UI.muted,
  border: UI.border,
  link: UI.link,
};

const MARKET = "FR";

const BUCKETS: {
  key: ReceivableBucket;
  title: string;
  sub: string;
  pill: { bg: string; fg: string; label: string };
}[] = [
  {
    key: "cadencia_ajustada",
    title: "En cadencia — fase real importada",
    sub: "Mise en demeure o suspensión ya hechas fuera (HubSpot/ajuste); el motor continúa desde ahí.",
    pill: { bg: "#d4edda", fg: "#155724", label: "Cadencia" },
  },
  {
    key: "cadencia",
    title: "En cadencia — desde el principio",
    sub: "Relance-1 en la cola, con destinatario e importes pre-rellenados.",
    pill: { bg: "#d4edda", fg: "#155724", label: "Cadencia" },
  },
  {
    key: "disputado",
    title: "Disputados — deuda bloqueada",
    sub: "Un chargeback no se reclama (regla A2): va por confirmación + reemisión.",
    pill: { bg: "#f8d7da", fg: "#721c24", label: "Bloqueado" },
  },
  {
    key: "write_off",
    title: "Póliza INACTIVE con deuda antigua — condonar",
    sub: "El stock inicial (33 clientes, 3.294,63 €) se condonó el 10/08 — por eso este grupo suele estar vacío. Si aparece alguien aquí es deuda nueva de una póliza muerta: candidata a write-off (queda WRITTEN_OFF en el ledger de ms-payment, con actor y motivo).",
    pill: { bg: "#fff3cd", fg: "#856404", label: "Write-off" },
  },
  {
    key: "pendiente",
    title: "Sin episodio, póliza viva — decidir",
    sub: "Deuda real sin reclamación en curso: métela en cadencia o descártala con criterio.",
    pill: { bg: "#e2e3f1", fg: "#41417e", label: "Pendiente" },
  },
];

function eur(cents: number): string {
  return (cents / 100).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function ddmm(d: string | null): string {
  if (!d) return "—";
  const [y, m, day] = d.slice(0, 10).split("-");
  return `${day}/${m}/${y!.slice(2)}`;
}

const TPL_LABEL: Record<string, string> = {
  "relance-1-impaye-d2c-fr": "relance-1",
  "mise-en-demeure-d2c-fr": "mise en demeure",
  "rappel-impaye-d2c-fr": "rappel",
  "suspension-garanties-d2c-fr": "suspensión",
  "resiliation-definitive-d2c-fr": "résiliation",
};

/**
 * El flujo FR v1, para el repaso visual de cabecera: días NATURALES contados
 * desde el ancla del episodio (su apertura, o la fase real si Ops la ajustó).
 * El recuento por escalón sale de las tareas pendientes vivas — no es un
 * gráfico, es dónde está cada cliente AHORA.
 */
const FLOW_STEPS: { tpl: string; label: string; day: number }[] = [
  { tpl: "relance-1-impaye-d2c-fr", label: "Relance 1", day: 0 },
  { tpl: "mise-en-demeure-d2c-fr", label: "Mise en demeure", day: 10 },
  { tpl: "rappel-impaye-d2c-fr", label: "Rappel", day: 20 },
  { tpl: "suspension-garanties-d2c-fr", label: "Suspension", day: 40 },
  { tpl: "resiliation-definitive-d2c-fr", label: "Résiliation", day: 50 },
];

export default function Cartera() {
  const { token } = useAuth();
  const [data, setData] = useState<ReceivablesOverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** Lead con el diálogo de condonación abierto + su motivo. */
  const [condoning, setCondoning] = useState<ReceivableRow | null>(null);
  const [motivo, setMotivo] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await getReceivablesOverview(MARKET, { token }));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const openEpisode = useCallback(
    async (row: ReceivableRow) => {
      setBusy(row.leadUlid);
      setNotice(null);
      try {
        const res = await openEpisodeForLead(row.leadUlid, MARKET, { token });
        setNotice(
          res.created
            ? `Episodio abierto para ${res.subscriptionRef}. Su tarea aparecerá en la cola en la próxima pasada del motor (~10 min).`
            : `${res.subscriptionRef} ya tenía un episodio abierto.`,
        );
        await load();
      } catch (err) {
        window.alert(
          `No se pudo abrir el episodio: ${err instanceof Error ? err.message : String(err)}`,
        );
      } finally {
        setBusy(null);
      }
    },
    [token, load],
  );

  const doWriteOff = useCallback(async () => {
    if (!condoning || motivo.trim().length < 5) return;
    setBusy(condoning.leadUlid);
    setNotice(null);
    try {
      const alive =
        (condoning.subscriptionStatus ?? "").toUpperCase() !== "INACTIVE";
      const res = await writeOffLead(
        condoning.leadUlid,
        MARKET,
        { motivo: motivo.trim(), ...(alive ? { condonarPolizaViva: true } : {}) },
        { token },
      );
      setNotice(
        `Condonados ${res.receiptsWrittenOff} recibo(s) · ${eur(res.centsWrittenOff)} € — queda en el ledger de ms-payment con tu nombre y el motivo.` +
          (res.receiptsFailed.length > 0
            ? ` ⚠ ${res.receiptsFailed.length} recibo(s) rechazados por ms-payment: avisa a tecnología.`
            : ""),
      );
      setCondoning(null);
      setMotivo("");
      await load();
    } catch (err) {
      window.alert(
        `No se pudo condonar: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setBusy(null);
    }
  }, [condoning, motivo, token, load]);

  const grouped = useMemo(() => {
    const map = new Map<ReceivableBucket, ReceivableRow[]>();
    for (const b of BUCKETS) map.set(b.key, []);
    for (const r of data?.rows ?? []) map.get(r.bucket)?.push(r);
    return map;
  }, [data]);

  const flowCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of data?.rows ?? []) {
      if (r.currentTemplateId) {
        counts.set(r.currentTemplateId, (counts.get(r.currentTemplateId) ?? 0) + 1);
      }
    }
    return counts;
  }, [data]);

  if (error) {
    return (
      <div style={{ padding: "1rem 1.5rem", maxWidth: 1600, margin: "0 auto" }}>
        <RecobrosTabs />
        <ErrorBanner message={error} />
      </div>
    );
  }

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1600, margin: "0 auto" }}>
      <RecobrosTabs />
      <h2 style={{ margin: 0, fontSize: "1.15rem", color: COLORS.heading }}>
        Deuda por cliente · {MARKET}
      </h2>
      <p style={{ color: COLORS.muted, fontSize: ".85rem", marginTop: 0 }}>
        En vivo desde el ledger de ms-payment (refresco ≤ 60 s). Reconcilia al céntimo
        por construcción.{" "}
        <Link to="/operations/recobros/condonados" style={{ color: COLORS.link }}>
          Ver deuda perdonada →
        </Link>
      </p>

      {/* Repaso visual del flujo: dónde está cada cliente AHORA */}
      <div
        style={{
          overflowX: "auto",
          border: `1px solid ${COLORS.border}`,
          borderRadius: ".55rem",
          background: "#fff",
          padding: ".85rem 1rem",
          margin: "1rem 0 0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", minWidth: 640 }}>
          {FLOW_STEPS.map((step, i) => {
            const n = flowCounts.get(step.tpl) ?? 0;
            const last = step.tpl === "resiliation-definitive-d2c-fr";
            return (
              <div key={step.tpl} style={{ display: "flex", alignItems: "center", flex: i < FLOW_STEPS.length - 1 ? 1 : "0 0 auto" }}>
                <div style={{ textAlign: "center", minWidth: "6.5rem" }}>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "1.9rem",
                      height: "1.9rem",
                      padding: "0 .4rem",
                      borderRadius: "999px",
                      fontWeight: 700,
                      fontSize: ".85rem",
                      background: n > 0 ? (last ? "#f8d7da" : "#d4edda") : "#f1f3f5",
                      color: n > 0 ? (last ? "#721c24" : "#155724") : COLORS.muted,
                      border: `1px solid ${n > 0 ? (last ? "#f5c6cb" : "#c3e6cb") : COLORS.border}`,
                    }}
                    title={`${n} cliente(s) con tarea de ${step.label} pendiente`}
                  >
                    {n}
                  </div>
                  <div style={{ fontSize: ".78rem", fontWeight: 600, color: COLORS.heading, marginTop: ".25rem", whiteSpace: "nowrap" }}>
                    {step.label}
                  </div>
                  <div style={{ fontSize: ".7rem", color: COLORS.muted }}>D+{step.day}</div>
                </div>
                {i < FLOW_STEPS.length - 1 && (
                  <div
                    aria-hidden="true"
                    style={{
                      flex: 1,
                      height: 2,
                      background: COLORS.border,
                      margin: "0 .35rem",
                      position: "relative",
                      top: "-1.1rem",
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
        <p style={{ color: COLORS.muted, fontSize: ".72rem", margin: ".5rem 0 0" }}>
          Días naturales desde el ancla del episodio (su apertura, o la fase real si se
          ajustó). El número = clientes con esa carta pendiente de enviar ahora. Los plazos
          legales del art. L.113-3 corren desde la mise en demeure real.
        </p>
      </div>

      {/* Chips-resumen: el seguimiento de un vistazo */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: ".6rem", margin: "1rem 0 1.6rem" }}>
        {BUCKETS.map((b) => {
          const t = data?.totals?.[b.key];
          return (
            <div
              key={b.key}
              style={{
                border: `1px solid ${COLORS.border}`,
                borderRadius: ".5rem",
                padding: ".5rem .8rem",
                minWidth: "9rem",
                background: "#fff",
              }}
            >
              <div style={{ fontSize: "1.3rem", fontWeight: 700, color: b.pill.fg }}>
                {t ? t.clients : "…"}
              </div>
              <div
                style={{
                  fontSize: ".7rem",
                  textTransform: "uppercase",
                  letterSpacing: ".04em",
                  color: COLORS.muted,
                }}
              >
                {b.pill.label === "Cadencia"
                  ? b.key === "cadencia_ajustada"
                    ? "Fase ajustada"
                    : "En cadencia"
                  : b.title.split(" — ")[0]}
              </div>
              <div style={{ fontSize: ".8rem", color: COLORS.muted }}>
                {t ? `${eur(t.debtCents)} €` : ""}
              </div>
            </div>
          );
        })}
      </div>

      {notice && (
        <div
          style={{
            padding: ".55rem .8rem",
            background: "#d4edda",
            color: "#155724",
            borderRadius: ".45rem",
            marginBottom: "1rem",
            fontSize: ".85rem",
          }}
        >
          {notice}
        </div>
      )}

      {BUCKETS.map((b) => {
        const rows = grouped.get(b.key) ?? [];
        if (!data) return null;
        return (
          <section key={b.key} style={{ marginBottom: "2rem" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: ".7rem" }}>
              <h2 style={{ fontSize: "1.05rem", color: COLORS.heading, margin: 0 }}>{b.title}</h2>
              <span style={{ color: COLORS.muted, fontSize: ".82rem" }}>
                {rows.length} clientes · {eur(rows.reduce((s, r) => s + r.debtCents, 0))} €
              </span>
            </div>
            <p style={{ color: COLORS.muted, fontSize: ".82rem", margin: ".2rem 0 .6rem" }}>
              {b.sub}
            </p>
            {rows.length === 0 ? (
              <p style={{ color: COLORS.muted, fontSize: ".82rem" }}>
                {b.key === "write_off"
                  ? "Vacío: todo lo condonable está ya condonado en el ledger (historial en ms-payment, R7)."
                  : "Nadie en este grupo."}
              </p>
            ) : (
              <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: ".5rem" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 860, fontSize: ".85rem" }}>
                  <thead>
                    <tr>
                      {["Póliza", "Deuda €", "Recibos", "Desde", "Situación", "Acción"].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: h === "Deuda €" ? "right" : "left",
                            fontSize: ".72rem",
                            textTransform: "uppercase",
                            letterSpacing: ".04em",
                            color: COLORS.muted,
                            background: "#f8f9fa",
                            padding: ".5rem .65rem",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.leadUlid}>
                        {/*
                          La póliza identifica la fila, pero el CLIENTE tiene
                          que ser alcanzable SIEMPRE: antes solo se enlazaba
                          cuando faltaba la referencia de póliza, así que en
                          las filas mejor identificadas el cliente quedaba
                          fuera de alcance. Y con `Link`, no con `href`
                          absoluto: un `href` a operations.life5.com saca de la
                          aplicación (y en local/staging te manda a producción).
                        */}
                        <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}`, fontWeight: 600, whiteSpace: "nowrap" }}>
                          <Link
                            to={`/leads/${r.leadUlid}`}
                            style={{ color: COLORS.link, textDecoration: "none" }}
                          >
                            {r.subscriptionRef ?? (
                              <span style={{ fontFamily: "monospace", fontSize: ".78rem", fontWeight: 400 }}>
                                {r.leadUlid.slice(0, 8)}…
                              </span>
                            )}
                          </Link>
                        </td>
                        <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}`, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                          {r.debtCents > 0 ? eur(r.debtCents) : `bloq. ${eur(r.blockedCents)}`}
                        </td>
                        <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}` }}>{r.receipts || "—"}</td>
                        <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}`, whiteSpace: "nowrap" }}>{ddmm(r.oldestExpected)}</td>
                        <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}`, color: COLORS.muted }}>
                          {r.taskId && r.currentTemplateId
                            ? `Tarea de ${TPL_LABEL[r.currentTemplateId] ?? r.currentTemplateId} (vence ${ddmm(r.taskDueAt)})`
                            : r.bucket === "disputado"
                              ? `Disputa viva${r.blockedCents > 0 ? ` · ${eur(r.blockedCents)} € bloqueados` : ""}`
                              : r.episodeId
                                ? "En cadencia — siguiente escalón en preparación"
                                : `Póliza ${r.subscriptionStatus ?? "?"} · último recibo ${ddmm(r.newestExpected)}`}
                        </td>
                        <td style={{ padding: ".45rem .65rem", borderTop: `1px solid ${COLORS.border}`, whiteSpace: "nowrap" }}>
                          {r.taskId ? (
                            <Link
                              to={`/operations/recobros/tareas?episodeId=${r.episodeId ?? ""}`}
                              style={{ color: COLORS.link }}
                            >
                              Ir a la tarea →
                            </Link>
                          ) : r.bucket === "pendiente" ? (
                            <span style={{ display: "inline-flex", gap: ".4rem" }}>
                              <button
                                type="button"
                                disabled={busy === r.leadUlid}
                                onClick={() => void openEpisode(r)}
                                style={{
                                  border: `1px solid ${COLORS.link}`,
                                  color: COLORS.link,
                                  background: "transparent",
                                  borderRadius: ".4rem",
                                  padding: ".2rem .6rem",
                                  cursor: "pointer",
                                  fontSize: ".8rem",
                                }}
                              >
                                {busy === r.leadUlid ? "Abriendo…" : "Meter en cadencia"}
                              </button>
                              <button
                                type="button"
                                disabled={busy === r.leadUlid}
                                onClick={() => {
                                  setCondoning(r);
                                  setMotivo("");
                                }}
                                style={{
                                  border: "1px solid #856404",
                                  color: "#856404",
                                  background: "transparent",
                                  borderRadius: ".4rem",
                                  padding: ".2rem .6rem",
                                  cursor: "pointer",
                                  fontSize: ".8rem",
                                }}
                              >
                                Condonar…
                              </button>
                            </span>
                          ) : r.bucket === "write_off" ? (
                            <button
                              type="button"
                              disabled={busy === r.leadUlid}
                              onClick={() => {
                                setCondoning(r);
                                setMotivo("");
                              }}
                              style={{
                                border: "1px solid #856404",
                                color: "#856404",
                                background: "transparent",
                                borderRadius: ".4rem",
                                padding: ".2rem .6rem",
                                cursor: "pointer",
                                fontSize: ".8rem",
                              }}
                            >
                              Condonar…
                            </button>
                          ) : r.episodeId ? (
                            <Link
                              to={`/operations/recobros/${r.episodeId}`}
                              style={{ color: COLORS.link }}
                            >
                              Ver episodio →
                            </Link>
                          ) : (
                            <span style={{ color: COLORS.muted }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}

      {condoning && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: ".6rem",
              padding: "1.2rem 1.4rem",
              maxWidth: 460,
              width: "92%",
              boxShadow: "0 8px 30px rgba(0,0,0,.2)",
            }}
          >
            <h3 style={{ margin: "0 0 .4rem", color: COLORS.heading, fontSize: "1.05rem" }}>
              Condonar la deuda de {condoning.subscriptionRef ?? condoning.leadUlid.slice(0, 8) + "…"}
            </h3>
            <p style={{ color: COLORS.muted, fontSize: ".85rem", margin: "0 0 .7rem" }}>
              Se perdonan {condoning.receipts} recibo(s) por {eur(condoning.debtCents)} €. Queda
              registrado en el ledger de ms-payment con tu nombre y este motivo — no es
              reversible desde aquí.
            </p>
            {(condoning.subscriptionStatus ?? "").toUpperCase() !== "INACTIVE" && (
              <p
                style={{
                  background: "#fff3cd",
                  color: "#856404",
                  borderRadius: ".4rem",
                  padding: ".45rem .6rem",
                  fontSize: ".8rem",
                  margin: "0 0 .7rem",
                }}
              >
                ⚠ La póliza está {condoning.subscriptionStatus ?? "en estado desconocido"} (viva):
                vas a perdonar deuda de un cliente activo en vez de reclamarla.
              </p>
            )}
            <label style={{ display: "grid", gap: ".25rem", marginBottom: ".8rem" }}>
              <span style={{ fontSize: ".78rem", color: COLORS.muted }}>
                Motivo (obligatorio)
              </span>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={2}
                placeholder="p.ej. póliza inactiva desde 2023, deuda incobrable"
                style={{
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: ".4rem",
                  padding: ".45rem .6rem",
                  fontSize: ".85rem",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
              />
            </label>
            <div style={{ display: "flex", gap: ".5rem", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setCondoning(null)}
                disabled={busy === condoning.leadUlid}
                style={{
                  border: `1px solid ${COLORS.border}`,
                  background: "transparent",
                  color: COLORS.heading,
                  borderRadius: ".4rem",
                  padding: ".35rem .8rem",
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void doWriteOff()}
                disabled={motivo.trim().length < 5 || busy === condoning.leadUlid}
                style={{
                  border: "1px solid #856404",
                  background: motivo.trim().length >= 5 ? "#856404" : "#c9b37a",
                  color: "#fff",
                  borderRadius: ".4rem",
                  padding: ".35rem .8rem",
                  cursor: motivo.trim().length >= 5 ? "pointer" : "not-allowed",
                }}
              >
                {busy === condoning.leadUlid ? "Condonando…" : "Condonar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
